import {
  Box,
  Paper,
  Tab,
  TableContainer,
  Tabs,
  Typography
} from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Chip from '../../components/Chip/Chip';
import DefaultTable from '../../components/DefaultTable/DefaultTable';
import TableToolbar from '../../components/DefaultTable/TableToolbar/TableToolbar';
import FormatCurrency from '../../components/FormatNumber/FormatCurrency';
import GlobalSearch from '../../components/GlobalSearch/GlobalSearch';
import PageTitle from '../../components/PageTitle/PageTitle';
import { textEllipsis } from '../../constants';
import { ProjectPaymentType } from '../../enums/projectPaymentType';
import { ProjectStatus } from '../../enums/projectStatus';
import { useShowFilterOrSearch } from '../../hooks/useShowFilterOrSearch';
import { useTableFunctions } from '../../hooks/useTableFunctions';
import { HeadCell } from '../../models/headCell';
import { Pagination } from '../../models/pagination';
import { Project } from '../../models/project';
import { TalentDashboardNotification } from '../../models/talentDashboardNotification';
import { PROJECTS } from '../../router/constants';
import { dashboardService } from '../../services/dashboardService';
import { projectService } from '../../services/projectService';
import {
  createDownloadFile,
  removeEmptyObjectFields
} from '../../utils/helpers';
import { getProjectStatusStyle } from '../../utils/projectStatus';
import FilterProjects, { FilterProjectsForm } from '../Projects/FilterProjects';
import NotificationsList from './NotificationsList';

const initialProjectsColumnsDataTalent: HeadCell[] = [
  {
    id: 'status',
    label: 'Project Status',
    sortable: true,
    visible: true,
    tdStyle: { padding: '11px' },
    thStyle: { width: 150, padding: '11px', textAlign: 'center' },
    render: (project: Project) => (
      <Box sx={getProjectStatusStyle(ProjectStatus[project.status])}>
        {ProjectStatus[project.status]}
      </Box>
    )
  },
  {
    id: 'id',
    label: 'Project #',
    sortable: true,
    visible: true,
    tdStyle: { padding: '11px' },
    thStyle: { padding: '11px' },
    render: (project: Project) => project.id
  },
  {
    id: 'projectTitle',
    label: 'Title',
    sortable: true,
    visible: true,
    tdStyle: { padding: '11px' },
    thStyle: { padding: '11px' },
    render: (project: Project) => (
      <Box sx={{ ...textEllipsis, maxWidth: 200 }}>{project.projectTitle}</Box>
    )
  },
  {
    id: 'company',
    label: 'Company',
    sortable: true,
    visible: true,
    tdStyle: { padding: '11px' },
    thStyle: { width: 100, padding: '11px' },
    render: (project: Project) => project.companyName
  },
  {
    id: 'timeline',
    label: 'Timeline',
    sortable: true,
    visible: true,
    tdStyle: { padding: '11px' },
    thStyle: { padding: '11px' },
    render: (project: Project) =>
      project.startDate &&
      project.endDate &&
      format(new Date(project.startDate), 'MMM do') +
        ' - ' +
        format(new Date(project.endDate), 'MMM do')
  },
  {
    id: 'paymentType',
    label: 'Payment Type',
    sortable: true,
    visible: true,
    tdStyle: { padding: '11px' },
    thStyle: { padding: '11px' },
    render: (project: Project) => ProjectPaymentType[project.paymentType]
  },
  {
    id: 'budget',
    label: 'Budget',
    sortable: true,
    visible: true,
    tdStyle: { padding: '11px' },
    thStyle: { width: 100, padding: '11px' },
    render: (project: Project) =>
      project.budget || project.pricePerHour ? (
        <>
          <FormatCurrency value={project.pricePerHour ?? project.budget} />
          {ProjectPaymentType[project.paymentType] ===
          ProjectPaymentType.HOURLY_RATE
            ? '/hour'
            : null}
        </>
      ) : null
  },
  {
    id: 'amountOpen',
    label: 'Amount Open',
    sortable: true,
    visible: true,
    tdStyle: { padding: '11px' },
    thStyle: { padding: '11px' },
    render: (project: Project) =>
      (ProjectStatus[project.status] === ProjectStatus.COMPLETED ||
        ProjectStatus[project.status] === ProjectStatus.IN_PROGRESS) &&
      project.amountOpen ? (
        <FormatCurrency value={project.amountOpen ?? null} />
      ) : (
        '--'
      )
  },
  {
    id: 'company.name',
    label: 'Company',
    sortable: true,
    visible: true,
    tdStyle: { padding: '11px' },
    thStyle: { padding: '11px' },
    render: (project: Project) => project.talent?.company.name
  }
];

const Dashboard = (): JSX.Element => {
  const initialFilterData: FilterProjectsForm = {
    startDate: null,
    endDate: null,
    projectStatuses: [],
    id: '',
    projectTitle: '',
    category: '',
    paymentType: '',
    budget: '',
    amountOpen: '',
    talentIds: []
  };
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const [paginationProjects, setPaginationProjects] = useState<
    Pagination<Project> | undefined
  >(undefined);
  const [loading, setLoading] = useState(true);
  const [tableTitle, setTableTitle] = useState('View Current Projects');
  const [tabsValue, setTabsValue] = React.useState('current');
  const [dashboardInfo, setDashboardInfo] =
    useState<TalentDashboardNotification>();
  const [showFilters, showSearch, onFilterClick, onSearchClick] =
    useShowFilterOrSearch();
  const {
    columnsData,
    page,
    rowsPerPage,
    sortBy,
    sortType,
    searchTerm,
    filterData,
    onRequestSort,
    onChangePage,
    onChangeRowsPerPage,
    onChangeColumnVisibility,
    onSearchChange,
    onFilterChange
  } = useTableFunctions<FilterProjectsForm>(
    initialProjectsColumnsDataTalent,
    initialFilterData,
    'acceptDate',
    'desc'
  );

  useEffect(() => {
    setTableTitle(
      tabsValue === 'current' ? 'View Current Projects' : 'Project History'
    );
    getProjects();
  }, [tabsValue, page, rowsPerPage, sortBy, sortType, searchTerm, filterData]);

  useEffect(() => {
    getNotifications();
  }, []);

  const getProjects = () => {
    projectService
      .getProjects(generateFilterBody(), {
        params: {
          page: page,
          size: rowsPerPage,
          sort: `${sortBy === 'timeline' ? 'startDate' : sortBy},${sortType}`
        }
      })
      .then((response) => {
        setPaginationProjects(response.data);
        setLoading(false);
      });
  };

  const getNotifications = () => {
    dashboardService.getTalentNotifications().then((payload) => {
      setDashboardInfo(payload.data);
    });
  };

  const handleTabStyle = (tab: string) => {
    return {
      borderRadius: tabsValue === tab ? '7px 7px 0px 0px' : 'unset',
      background: tabsValue === tab ? 'white' : 'unset',
      boxShadow:
        tabsValue === tab
          ? `0px 2px 1px -1px rgb(0 0 0 / 20%), 
         0px 1px 1px 0px rgb(0 0 0 / 14%), 
         0px 1px 3px 0px rgb(0 0 0 / 12%)`
          : 'unset'
    };
  };

  const handleTableStyle = (tab: string) => {
    return {
      minHeight: '160px',
      borderRadius: tabsValue === tab ? '0px 4px 4px 4px' : '4px'
    };
  };

  const handleExport = () => {
    projectService.exportProjects(generateFilterBody()).then((response) => {
      createDownloadFile(response.data, 'projects');
    });
  };

  const generateFilterBody = () => {
    let body = removeEmptyObjectFields(filterData);
    if (body.talentIds) {
      body = {
        ...body,
        talentIds: filterData.talentIds
          ? filterData.talentIds.map((talent) => talent.id)
          : []
      };
    }
    if (searchTerm) {
      body = {
        ...body,
        searchTerm: searchTerm
      };
    }

    const status = tabsValue !== 'current' ? 'COMPLETED' : 'IN_PROGRESS';

    return { ...body, projectStatuses: [status] };
  };

  return (
    <>
      <PageTitle>Welcome back, {keycloak.tokenParsed?.given_name}</PageTitle>
      <NotificationsList
        projects={dashboardInfo?.inProgress}
        requests={dashboardInfo?.requests}
        invoices={dashboardInfo?.openInvoices}
      />
      <Tabs
        value={tabsValue}
        onChange={(event, value) => {
          setTabsValue(value);
        }}
        textColor="primary"
        TabIndicatorProps={{
          style: {
            display: 'none'
          }
        }}
        aria-label="primary tabs example"
      >
        <Tab
          value="current"
          label="Current Projects"
          disableRipple
          disableTouchRipple
          sx={handleTabStyle('current')}
        />
        <Tab
          value="history"
          disableRipple
          disableTouchRipple
          label="Project History"
          sx={handleTabStyle('history')}
        />
      </Tabs>
      <TableContainer sx={handleTableStyle('current')} component={Paper}>
        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            ml: 3,
            mt: 3
          }}
        >
          <Typography variant="h5" color="#333333" mb={3}>
            {tableTitle}
          </Typography>
          <Chip size={paginationProjects?.numberOfElements} />
        </Box>
        <TableToolbar
          viewTableColumnsProps={{
            columns: columnsData,
            onColumnClick: onChangeColumnVisibility
          }}
          onFilter={onFilterClick}
          onSearch={onSearchClick}
          onExport={handleExport}
        >
          {showSearch && (
            <GlobalSearch
              onSearchChange={onSearchChange}
              searchTerm={searchTerm}
            />
          )}
          {showFilters && (
            <FilterProjects
              handleFormSubmit={onFilterChange}
              filterData={filterData}
            />
          )}
        </TableToolbar>
        <DefaultTable
          columns={columnsData}
          data={paginationProjects?.content}
          rowClick={(project: Project) => navigate(`${PROJECTS}/${project.id}`)}
          sortProps={{ sortBy, sortType, onRequestSort }}
          paginationProps={{
            page,
            rowsPerPage,
            onChangePage,
            onChangeRowsPerPage,
            totalElements: paginationProjects?.totalElements
          }}
        />
        {!loading && !paginationProjects?.content.length && (
          <Typography variant="h5" align="center" m={4}>
            No projects found!
          </Typography>
        )}
      </TableContainer>
    </>
  );
};

export default Dashboard;
